import { MediaFile, MediaType } from "@shared/types/media.types";
import React, { useCallback, useState } from "react";

interface ImageUploaderProps {
  imageUrl?: string;
  onImageSelected: (file: MediaFile) => void;
  onImageRemoved?: () => void;
  className?: string;
  roundedClass?: string;
  disabled?: boolean;
  isUploading?: boolean;
  loadingPosition?: "right" | "bottom";
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  imageUrl,
  onImageSelected,
  onImageRemoved,
  className = "",
  roundedClass = "rounded-full",
  disabled = false,
  isUploading = false,
  loadingPosition = "right",
}) => {
  const [dragActive, setDragActive] = useState(false);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  // Handle file input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, []);

  // Process the selected file
  const handleFile = useCallback(
    (file: File) => {
      // Only accept image files
      if (!file.type.startsWith("image/")) {
        alert("Only image files are allowed");
        return;
      }

      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;

        onImageSelected({
          file,
          preview,
          type: MediaType.IMAGE,
          progress: 0,
        });
      };
      reader.readAsDataURL(file);
    },
    [onImageSelected]
  );

  // Remove the current image
  const handleRemove = useCallback(() => {
    onImageRemoved?.();
  }, [onImageRemoved]);

  return (
    <div
      className={`flex items-center gap-3 ${
        loadingPosition === "right" ? "" : "flex-col"
      }`}
    >
      <div className={`relative ${className}`} onDragEnter={handleDrag}>
        <input
          type="file"
          id="image-upload"
          className="hidden"
          accept="image/*"
          onChange={handleChange}
          disabled={disabled}
        />

        {imageUrl ? (
          <div className="relative group">
            <img
              src={imageUrl}
              alt="Profile Image"
              className={`object-cover ${roundedClass} ${
                className || "w-full h-full"
              }`}
            />
            {!disabled && (
              <div
                className={`absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity ${roundedClass}`}
              >
                <button
                  type="button"
                  onClick={handleRemove}
                  className={`text-white p-2 ${roundedClass} bg-red-500 hover:bg-red-600`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ) : (
          <label
            htmlFor="image-upload"
            className={`flex flex-col items-center justify-center w-full h-full border-2 border-dashed ${roundedClass} cursor-pointer transition-colors ${
              dragActive
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600"
            }`}
          >
            <div className="flex flex-col items-center justify-center p-4 w-[120px] h-[120px]">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Drag an image or click to select
              </p>
            </div>
          </label>
        )}

        {dragActive && (
          <div
            className={`absolute inset-0 w-full h-full ${roundedClass}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          ></div>
        )}
      </div>
      {isUploading && (
        <div className="text-sm text-blue-600 dark:text-blue-400 my-2 flex items-center">
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Uploading...
        </div>
      )}
    </div>
  );
};
